import React, { ReactElement, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import type { MenuItemProps } from '@rio-cloud/rio-uikit/MenuItem';
import SimpleButtonDropdown from '@rio-cloud/rio-uikit/SimpleButtonDropdown';

import { DEFAULT_LOCALE } from '@/configuration/lang/lang';
import { getLocale } from '@/configuration/lang/langSlice';
import { useLazyDownloadMaintenanceHistoryQuery } from '@/features/history/api/maintenanceHistoryApi';

import { gaPush } from '@/configuration/setup/googleAnalytics';

type ExportOptionConfig = {
    format: string;
    icon: string;
    mimeType: string;
};

export const toPdfOption: ExportOptionConfig = {
    icon: 'rioglyph-pdf-file',
    format: 'pdf',
    mimeType: 'application/pdf',
};

export const toExcelOption: ExportOptionConfig = {
    icon: 'rioglyph-files',
    format: 'excel',
    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

export interface ExportDropdownButtonProps {
    assetId: string;
    options: ExportOptionConfig[];
    header?: string;
}

const ExportDropdownButton = ({ assetId, options, header }: ExportDropdownButtonProps): ReactElement => {
    const [triggerDownload, { isFetching }] = useLazyDownloadMaintenanceHistoryQuery();
    const locale = useSelector(getLocale) ?? DEFAULT_LOCALE;
    const registerClickExportButtonEventGA = (option: string) => {
        gaPush({
            event: 'button_export',
            parameters: {
                element_name: 'ExportDropdownButton',
                component_name: 'ButtonDropdown',
                trigger: 'click',
                element_state: option,
            },
            userProps: {},
        });
    };
    const itemsMapper = useCallback(
        ({ format, icon, mimeType }: ExportOptionConfig): MenuItemProps => {
            return {
                value: <ExportButton format={format} icon={icon} />,
                onSelect: () => {
                    triggerDownload({
                        assetId,
                        params: {
                            locale,
                        },
                        headers: {
                            Accept: mimeType,
                        },
                    });
                    registerClickExportButtonEventGA(format);
                },
            };
        },
        [assetId, locale],
    );
    const items: MenuItemProps[] = [];
    if (header) {
        items.push({
            value: <FormattedMessage id={header} />,
            header: true,
        });
    }
    items.push(...options.map(itemsMapper));
    return (
        <SimpleButtonDropdown
            items={items}
            disabled={isFetching}
            iconOnly
            title={<span className="rioglyph rioglyph-download" />}
            dropdownClassName="download-menu"
        />
    );
};

type ExportButtonProps = {
    format: string;
    icon: string;
};

const ExportButton = ({ format, icon }: ExportButtonProps) => (
    <span className="display-flex gap-5 align-items-center">
        <span className={`rioglyph ${icon}`} />
        <FormattedMessage id={`intl-msg:maintenanceHistory.exportDropdownMenu.${format}`} />
    </span>
);

export default ExportDropdownButton;
